import styled, { css } from "styled-components";
import { Icon, Loader, Popup } from "grabcad-ui-elements";

export interface VersionChangeIndicatorProps {
    currentVersion: string;
    isLoadingNextVersion?: boolean;
    errorNextVersion?: string;
    nextVersion?: string;
}

export function VersionChangeIndicator({
    currentVersion,
    nextVersion,
    isLoadingNextVersion = false,
    errorNextVersion,
}: VersionChangeIndicatorProps) {
    return (
        <Container>
            <Tag
                variant={nextVersion || isLoadingNextVersion ? "old" : undefined}
                aria-label="Current Version"
            >
                {currentVersion}
            </Tag>

            {(nextVersion || isLoadingNextVersion || errorNextVersion) && (
                <Arrow data-testid={`version-change-arrow`} aria-hidden="true">
                    🡲
                </Arrow>
            )}

            {isLoadingNextVersion && !errorNextVersion && (
                <Loader
                    active
                    inline
                    size="mini"
                    role="progressbar"
                    aria-busy="true"
                    aria-label="Loading content..."
                />
            )}
            {!isLoadingNextVersion && errorNextVersion && (
                <div role="alert">
                    <Popup
                        trigger={<Icon name="exclamation circle" color="red" />}
                        content={errorNextVersion}
                        position="right center"
                    />
                </div>
            )}

            {nextVersion && !isLoadingNextVersion && !errorNextVersion && (
                <Tag variant="new" aria-label="Next Version">
                    {nextVersion}
                </Tag>
            )}
        </Container>
    );
}

const Container = styled.div<{ align?: "left" | "right" }>`
    display: flex;
    gap: 5px;
    align-items: center;
    justify-content: space-around;
`;

const Tag = styled.div<{ variant?: "old" | "new" }>`
    padding: 3px 6px;
    min-width: 60px;
    border-radius: 6px;
    font-weight: 600;
    background-color: #d5d5d5;
    display: flex;
    justify-content: center;

    ${(props) =>
        props.variant === "old" &&
        css`
            background-color: #f5f5f5;
            color: #6a6a6a;
        `};

    ${(props) =>
        props.variant === "new" &&
        css`
            background-color: #cce4ee;
        `};
`;

const Arrow = styled.div`
    font-size: 12px;
    line-height: 12px;
    color: #4a4a4a;
`;
